import {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";

import { useUIStore } from "../store";
import { Product, MenuData } from "../interfaces/domain/menu";
import { useOrderActions } from "./useOrderActions";
import { formatPrice } from "../utils/formatersPrecio";
import { findCategoryByProductId, findProductById } from "../utils/findInMenu";

export const usePlateModal = (
  plate: Product | null,
  onClose: () => void,
  cartItem?: any,
  menuData?: MenuData
) => {
  const { addItem, updateItem } = useOrderActions();

  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(cartItem?.quantity ?? 1);
  const [selection, setSelection] = useState<Record<string, number>>({});

  const setSection = useUIStore((s) => s.setSection);

  const plateCategory = useMemo(() => {
    if (!menuData || !plate) return null;
    return findCategoryByProductId(menuData.categories, plate.id);
  }, [menuData, plate?.id]);

  // Resetear estado cuando cambia el producto
  useEffect(() => {
    if (!plate?.id) return;

    setIsClosing(false);
    setIsLoading(false);

    if (cartItem) {
      setQuantity(cartItem.quantity);
      setSelection(cartItem.comboSelection ?
        Object.fromEntries(cartItem.comboSelection.map((i: any) => [i.id, i.quantity]))
        : {}
      );
    } else {
      setQuantity(1);
      setSelection({});
    }
  }, [plate?.id, cartItem?.cartItemId]);

  const isCombo = useMemo(
    () => (plate?.combo?.items?.length ?? 0) > 0,
    [plate?.combo]
  );

  const totalPrice = (plate?.price ?? 0) * quantity;

  const buttonText = useMemo(() => {
    if (isLoading) return cartItem ? "Modificando..." : "Agregando...";
    const action = cartItem ? "Modificar" : "Agregar";
    return `${action} ${formatPrice(totalPrice)}`;
  }, [isLoading, cartItem, totalPrice]);

  const handleStartClosing = useCallback(() => {
    setIsClosing(true);
    const targetSection = cartItem ? "cart" : "home";
    setSection(targetSection);
    window.history.replaceState({}, "", window.location.pathname);
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose, setSection, cartItem]);

  const comboSelectionFormatted = useMemo(() => {
    if (!isCombo || !plate) return undefined;

    // Aplanar choices de todos los items "select" del combo
    const allChoices = (plate.combo?.items ?? [])
      .filter((i: any) => i.type === "select")
      .flatMap((i: any) => i.choices ?? []);

    return Object.entries(selection).map(([id, qty]) => {
      const found = allChoices.find((c: any) => c.id === id)
        ?? (menuData ? findProductById(menuData.categories, id) : null);
      return { id, quantity: Number(qty), title: found?.title ?? "Producto" };
    });
  }, [isCombo, plate, selection, menuData]);

  const handleSubmit = () => {
    if (isLoading || !plate) return;

    if (isCombo) {
      const selectItem = plate?.combo?.items?.find((i: any) => i.type === "select");
      if (selectItem) {
        const totalSelected = Object.values(selection).reduce(
          (acc, value) => acc + Number(value), 0
        );
        if (totalSelected !== selectItem.quantity) return;
      }
    }

    setIsLoading(true);

    setTimeout(() => {
      const orderData: any = {
        ...plate,
        productId: plate.id,
        quantity,
        price: plate.price ?? 0,
        comboSelection: isCombo ? comboSelectionFormatted : undefined,
      };

      if (cartItem?.cartItemId) {
        updateItem(cartItem.cartItemId, orderData);
      } else {
        addItem(orderData);
      }

      handleStartClosing();
    }, 300);
  };

  return {
    quantity,
    setQuantity,
    selection,
    setSelection,
    isClosing,
    setIsClosing,
    setIsLoading, // ✅ expuesto para resetear desde PlateModal al abrirse
    handleStartClosing,
    handleSubmit,
    isCombo,
    totalPrice,
    isLoading,
    buttonText,
    plateCategory
  };
};
